import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { colors } from "../../constants/colors";
import * as UseStyle from "./map.style";
import ButtonCustom from "../button/buttonCustom";
import ZoneBox from "./zoneBox";
import StallBox from "./stallBox";

type Map = {
  mapData: any;
  updateMap?: (
    valZone: any,
    valStall: any,
    dataXY: any,
    ref: any,
    position: any,
    indexZone: number,
    indexStall: any
  ) => void;
  dragGridZone: any;
  enableResizingZone: boolean;
  disableDraggingZone: boolean;
  dragGridStall: any;
  enableResizingStall: boolean;
  disableDraggingStall: boolean;
  onClick?: (valStall: any, zoneName: string) => void;
  onClickEditZone?: (flag: boolean) => void
  rentStall?: any;
  /** opt-in: ส่งค่าตอนปล่อยเมาส์แทนที่จะส่งทุก tick ของ onResize
   *  ค่าปลายทางเท่ากันเพราะระหว่าง resize ทั้ง size และ position เป็น uncontrolled อยู่แล้ว
   *  ไม่ส่ง = พฤติกรรมเดิมทุกอย่าง */
  commitOnResizeStop?: boolean;
  /** opt-in: sync รายการแผงเมื่อ mapData เปลี่ยน (ของเดิมเช็ค `if (indexZone)` ทำให้โซนแรกไม่อัปเดต)
   *  ไม่ส่ง = พฤติกรรมเดิม เพื่อไม่ให้หน้าที่ refetch ระหว่างผู้ใช้เปิดดูแผงอยู่โดนสลับข้อมูลกลางคัน */
  syncStallOnMapDataChange?: boolean;
};

/** pure function ไม่พึ่ง props/state — อยู่นอก component จะได้ไม่ถูกสร้างใหม่ทุก render */
const checkStatusColor = (status: string) => {
  if (status == "OPEN") {
    return colors.themeThirdColor;
  } else if (status == "BLANK") {
    return colors.themeSecondColor;
  } else if (status == "RESERVE") {
    return colors.yellow;
  } else if (status == "CONTRACT") {
    return colors.sky;
  } else if (status == "NON_INFO") {
    return colors.danger;
  }
};

export default function Map(props: Map) {
  const [onClick, setOnclick] = useState<number | null>(null);
  const [ins, setsIn] = useState<boolean>(true);
  const [outs, setOuts] = useState<boolean>(true);
  const [check, setCheck] = useState<boolean>(false);
  const [dataZone, setDataZone] = useState<any[]>([])
  const [dataZoneName, setDataZoneName] = useState<any>()
  /** null = ยังไม่ได้เปิดโซนไหน (แสดงรายการโซน) · ตัวเลข = กำลังดูแผงของโซนนั้น
   *  เดิมใช้ตัวเลขตัวเดียวแล้วเช็ค `if (indexZone)` ซึ่งปนกันสองเรื่อง
   *  ทำให้แยกไม่ออกระหว่าง "ยังไม่ได้เปิด" กับ "เปิดโซนแรก (index 0)" */
  const [selectedZoneIndex, setSelectedZoneIndex] = useState<number | null>(null)
  const indexZone = selectedZoneIndex ?? 0

  /** เก็บค่าล่าสุดไว้ใน ref เพื่อให้ callback ที่ส่งลงไปให้ลูกเป็น identity เดิมได้
   *  (ถ้าใส่ค่าพวกนี้ใน deps ของ useCallback ลูกจะ re-render ทุกครั้งเหมือนเดิม) */
  const latest = useRef({ props, dataZone, dataZoneName, indexZone, selectedZoneIndex, check });
  latest.current = { props, dataZone, dataZoneName, indexZone, selectedZoneIndex, check };

  const click = useCallback((type: any) => {
    if (type == "OUT") {
      if (!latest.current.check) {
        setCheck(true);
      } else {
        setsIn(true);
        setOuts(false);
        setCheck(false);
      }
    } else if (type == "IN" && !latest.current.check) {
      setsIn(false);
      setOuts(true);
    }
  }, []);

  /** เดิมเป็น rentStall.some(... zone.stall.map(...).includes(...)) ซึ่งเป็น O(rentStall × stall)
   *  และสร้าง array ใหม่ทุกโซนทุก render */
  const rentStallIds = useMemo(() => {
    if (!props.rentStall) return null;
    return new Set(props.rentStall.map((r: any) => r?.stallId));
  }, [props.rentStall]);

  const getZoneBackground = useCallback(
    (zone: any) => {
      if (!rentStallIds) return colors.white;
      const found = (zone?.stall || []).some((s: any) => rentStallIds.has(s?.stallId));
      return found ? colors.white : colors.gray;
    },
    [rentStallIds]
  );

  useEffect(() => {
    if (!props.syncStallOnMapDataChange) {
      // พฤติกรรมเดิมทุกอย่างสำหรับหน้าที่ไม่ opt-in (index 0 เป็น falsy จึงไม่ sync)
      if (indexZone) setDataZone(props?.mapData[indexZone]?.stall)
      return;
    }
    // ต้องเช็ค null ไม่ใช่เช็ค falsy — ไม่งั้นพอ mapData มาถึงครั้งแรก
    // จะเด้งเข้าโหมดแผงของโซนแรกเองทั้งที่ผู้ใช้ยังไม่ได้กด "จัดการโซน"
    // และเด้งซ้ำทุกครั้งที่ลาก/ย่อขยายเพราะ updateMap เรียก setMapData
    if (selectedZoneIndex == null) return;
    const stalls = props?.mapData?.[selectedZoneIndex]?.stall;
    if (stalls) setDataZone(stalls);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.mapData]);

  /* ---------- handler ของแผง ---------- */

  const handleStallSelectAt = useCallback((indexStall: number) => {
    const { props: p, dataZone: stalls, dataZoneName: zoneName } = latest.current;
    if (p.onClick) p.onClick(stalls[indexStall], zoneName);
    click("IN");
    setCheck(false);
  }, [click]);

  const handleStallDragStop = useCallback((indexStall: number, d: any) => {
    const { props: p, dataZone: stalls, indexZone: zi } = latest.current;
    const valStall = stalls[indexStall];
    if (p?.updateMap) p.updateMap(valStall, valStall, d, null, null, zi, indexStall);
  }, []);

  const handleStallResizeCommit = useCallback(
    (indexStall: number, ref: any, position: any) => {
      const { props: p, dataZone: stalls, indexZone: zi } = latest.current;
      const valStall = stalls[indexStall];
      if (p?.updateMap) p.updateMap(valStall, valStall, null, ref, position, zi, indexStall);
    },
    []
  );

  /* ---------- handler ของโซน ---------- */

  const handleZoneSelect = useCallback((i: number) => {
    setOnclick(i);
    click("OUT");
  }, [click]);

  const handleZoneManage = useCallback((i: number) => {
    const { props: p } = latest.current;
    const valZone = p.mapData?.[i];
    if (p.onClickEditZone) p.onClickEditZone(true)
    setDataZone(valZone?.stall)
    setDataZoneName(valZone?.zoneName)
    setOnclick(i);
    click("OUT");
    setCheck(false);
    setSelectedZoneIndex(i)
    if (p?.updateMap)
      p.updateMap(valZone, valZone?.stall, { x: valZone?.x, y: valZone?.y }, null, null, i, null);
  }, [click]);

  const handleZoneDragStop = useCallback((i: number, d: any) => {
    const { props: p } = latest.current;
    const valZone = p.mapData?.[i];
    if (p?.updateMap) p.updateMap(valZone, valZone?.stall, d, null, null, i, null);
  }, []);

  const handleZoneResizeCommit = useCallback((i: number, ref: any, position: any) => {
    const { props: p } = latest.current;
    const valZone = p.mapData?.[i];
    if (p?.updateMap) p.updateMap(valZone, valZone?.stall, null, ref, position, i, null);
  }, []);

  const handleBack = useCallback(() => {
    const { props: p } = latest.current;
    if (p.onClickEditZone) p.onClickEditZone(false)
    setDataZone([])
    setSelectedZoneIndex(null)
  }, []);

  return (
    <div>
      <div
        style={{
          border: `1px solid ${colors.gray}`,
          width: "100%",
          height: 700,
          position: 'relative'
        }}
      >
        {props?.mapData && (
          dataZone?.length > 0 && (
            dataZone?.map((valStall: any, indexStall: number) => (
              <StallBox
                key={valStall?.stallId != null ? `s-${valStall.stallId}` : `s-new-${indexStall}`}
                index={indexStall}
                x={valStall.x}
                y={valStall.y}
                width={valStall.width}
                height={valStall.height}
                label={valStall.stallNumber}
                bgColor={checkStatusColor(valStall.status)}
                enableResizing={props.enableResizingStall}
                disableDragging={ins || props.disableDraggingStall}
                dragGrid={props.dragGridStall}
                commitOnStop={props.commitOnResizeStop}
                onSelect={handleStallSelectAt}
                onDragStop={handleStallDragStop}
                onResizeCommit={handleStallResizeCommit}
              />
            ))
          ) || (
            props.mapData?.map((valZone: any, indexZoneItem: number) => (
              <ZoneBox
                key={valZone?.zoneId != null ? `z-${valZone.zoneId}` : `z-new-${indexZoneItem}`}
                index={indexZoneItem}
                x={valZone.x}
                y={valZone.y}
                width={valZone.width}
                height={valZone.height}
                zoneName={valZone.zoneName}
                bgColor={getZoneBackground(valZone)}
                isSelected={onClick === indexZoneItem}
                enableResizing={props.enableResizingZone}
                disableDragging={outs || props.disableDraggingZone}
                dragGrid={props.dragGridZone}
                commitOnStop={props.commitOnResizeStop}
                onSelect={handleZoneSelect}
                onManage={handleZoneManage}
                onDragStop={handleZoneDragStop}
                onResizeCommit={handleZoneResizeCommit}
              />
            ))
          )
        )
        }
      </div>
      {dataZone?.length > 0 && (
        <div className="pt-2 d-flex justify-content-center">
          <ButtonCustom textButton="ย้อนกลับ" onClick={handleBack} />
        </div>
      )}
      <div className="d-flex justify-content-end py-2">
        <div className="d-flex">
          <UseStyle.BoxStatus color={colors.themeThirdColor} />
          <div className="px-3">ระบบเปิดให้จอง</div>
        </div>
        <div className="d-flex">
          <UseStyle.BoxStatus color={colors.themeSecondColor} />
          <div className="px-3">ว่าง</div>
        </div>
        <div className="d-flex">
          <UseStyle.BoxStatus color={colors.yellow} />
          <div className="px-3">ติดจอง</div>
        </div>
        <div className="d-flex">
          <UseStyle.BoxStatus color={colors.sky} />
          <div className="px-3">ทำสัญญาแล้ว</div>
        </div>
        <div className="d-flex">
          <UseStyle.BoxStatus color={colors.danger} />
          <div className="px-3">ข้อมูลไม่ครบถ้วน</div>
        </div>
        <div className="d-flex ">
          <UseStyle.BoxStatus color={colors.gray} />
          <div className="pl-3">พื้นที่ไม่ได้เปิดจอง</div>
        </div>
      </div>
    </div>
  );
}
